// src/components/charts/GradePerformanceAnalysis.jsx
import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Users, 
  BarChart3, 
  TrendingUp, 
  Award, 
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Target,
  AlertCircle,
  School
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { StatisticsCalculator } from '../../utils/statistics';
import { useAuth } from '../../contexts/AuthContext';
import PerformanceAnalysis from './PerformanceAnalysis';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Line, Doughnut, Radar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
);

const GradePerformanceAnalysis = () => {
  const { userProfile } = useAuth();
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('all');
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [allMarks, setAllMarks] = useState([]);
  const [filteredMarks, setFilteredMarks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'task', 'learner'
  const [aggregatedStats, setAggregatedStats] = useState({
    totalLearners: 0,
    totalTasks: 0,
    averageMark: 0,
    passRate: 0,
    topicsStrengths: [],
    topicsWeaknesses: [],
    performanceLevels: []
  });
  const [topicPerformance, setTopicPerformance] = useState({});

  // Get available grades and subjects from user profile
  const availableGrades = userProfile?.grades || [];
  const availableSubjects = userProfile?.subjects || [];
  const terms = ['all', 'Term 1', 'Term 2', 'Term 3', 'Term 4', 'Semester 1', 'Semester 2'];

  useEffect(() => {
    // Initialize with first grade and subject from profile if available
    if (availableGrades.length > 0 && selectedGrade === '') {
      setSelectedGrade(availableGrades[0]);
    }
    if (availableSubjects.length > 0 && selectedSubject === '') {
      setSelectedSubject(availableSubjects[0]);
    }
  }, [availableGrades, availableSubjects]);

  useEffect(() => {
    if (selectedGrade && selectedSubject) {
      loadData();
    }
  }, [selectedGrade, selectedSubject, selectedTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedTask(null);
      setSelectedLearner(null);
      
      // Get all tasks for the selected grade and subject
      const allTasks = await AssessmentService.getAssessmentTasks();
      const filteredTasks = allTasks.filter(task => 
        task.grade === selectedGrade && 
        task.subject === selectedSubject
      );
      setTasks(filteredTasks);
      
      // Get all submissions for these tasks
      const allSubmissions = [];
      for (const task of filteredTasks) {
        try {
          const taskSubmissions = await AssessmentService.getTaskSubmissions(task.id);
          allSubmissions.push(...taskSubmissions);
        } catch (error) {
          console.warn(`Error loading submissions for task ${task.id}:`, error);
        }
      }
      
      // Filter by term if not 'all'
      const termFilteredSubmissions = selectedTerm === 'all' 
        ? allSubmissions 
        : allSubmissions.filter(sub => sub.term === selectedTerm);
      
      setSubmissions(termFilteredSubmissions);
      
      // Get all marks for the filtered submissions
      const marksPromises = termFilteredSubmissions.map(sub => 
        AssessmentService.getLearnerMarks(sub.taskId, sub.id).catch(error => {
          console.warn(`Error loading marks for submission ${sub.id}:`, error);
          return [];
        })
      );
      
      const marksResults = await Promise.all(marksPromises);
      const allMarksFlat = marksResults.flat();
      setAllMarks(allMarksFlat);
      setFilteredMarks(allMarksFlat);
      
      // Get unique learners
      const uniqueLearners = [...new Map(allMarksFlat.map(mark => 
        [`${mark.learnerSurname}-${mark.learnerName}`, { 
          surname: mark.learnerSurname,
          name: mark.learnerName,
          marks: allMarksFlat.filter(m => 
            m.learnerSurname === mark.learnerSurname && 
            m.learnerName === mark.learnerName
          )
        }]
      )).values()];
      
      setLearners(uniqueLearners);
      
      // Calculate aggregated statistics
      calculateAggregatedStats(allMarksFlat, filteredTasks);
      
    } catch (err) {
      console.error('Error loading performance data:', err);
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const calculateAggregatedStats = (marks, tasks) => {
    if (marks.length === 0) {
      setAggregatedStats({
        totalLearners: 0,
        totalTasks: tasks.length,
        averageMark: 0,
        passRate: 0,
        topicsStrengths: [],
        topicsWeaknesses: [],
        performanceLevels: []
      });
      setTopicPerformance({});
      return;
    }

    // Unique learners count
    const uniqueLearners = new Set(marks.map(mark => 
      `${mark.learnerSurname}-${mark.learnerName}`
    ));

    // Average mark
    const averageMark = marks.reduce((sum, mark) => 
      sum + mark.percentageMark, 0) / marks.length;

    // Pass rate
    const passingMarks = marks.filter(mark => mark.percentageMark >= 50);
    const passRate = (passingMarks.length / marks.length) * 100;

    // Calculate performance levels
    const performanceLevels = [
      { level: 'Outstanding', range: '80-100%', count: 0 },
      { level: 'Excellent', range: '70-79%', count: 0 },
      { level: 'Proficient', range: '60-69%', count: 0 },
      { level: 'Competent', range: '50-59%', count: 0 },
      { level: 'Partially Achieved', range: '30-49%', count: 0 },
      { level: 'Not Achieved', range: '0-29%', count: 0 }
    ];

    marks.forEach(mark => {
      if (mark.percentageMark >= 80) performanceLevels[0].count++;
      else if (mark.percentageMark >= 70) performanceLevels[1].count++;
      else if (mark.percentageMark >= 60) performanceLevels[2].count++;
      else if (mark.percentageMark >= 50) performanceLevels[3].count++;
      else if (mark.percentageMark >= 30) performanceLevels[4].count++;
      else performanceLevels[5].count++;
    });

    // Analyze topic performance (using tasks' topics as a proxy)
    const topicPerformance = {};
    const topicCounts = {};

    marks.forEach(mark => {
      const task = tasks.find(t => t.id === mark.taskId);
      if (task?.topics) {
        task.topics.forEach(topic => {
          if (!topicPerformance[topic]) {
            topicPerformance[topic] = 0;
            topicCounts[topic] = 0;
          }
          topicPerformance[topic] += mark.percentageMark;
          topicCounts[topic]++;
        });
      }
    });

    // Calculate average performance by topic
    const topicAverages = {};
    Object.keys(topicPerformance).forEach(topic => {
      topicAverages[topic] = topicPerformance[topic] / topicCounts[topic];
    });

    // Set topic performance data for charts
    setTopicPerformance(topicAverages);

    // Sort topics by performance
    const sortedTopics = Object.entries(topicAverages).sort((a, b) => b[1] - a[1]);
    
    // Get top and bottom 3 topics (or less if fewer topics exist)
    const topicsStrengths = sortedTopics.slice(0, Math.min(3, sortedTopics.length)).map(([topic, avg]) => ({
      topic,
      averagePercentage: avg
    }));
    
    const topicsWeaknesses = sortedTopics.slice(-Math.min(3, sortedTopics.length)).reverse().map(([topic, avg]) => ({
      topic,
      averagePercentage: avg
    }));

    setAggregatedStats({
      totalLearners: uniqueLearners.size,
      totalTasks: tasks.length,
      averageMark,
      passRate,
      topicsStrengths,
      topicsWeaknesses,
      performanceLevels
    });
  };

  const handleTaskSelect = (task) => {
    setSelectedTask(task);
    setSelectedLearner(null);
    setViewMode('task');
  };

  const handleLearnerSelect = (learner) => {
    setSelectedLearner(learner);
    setSelectedTask(null);
    setViewMode('learner');
  };

  const handleBack = () => {
    if (viewMode === 'task' || viewMode === 'learner') {
      setViewMode('overview');
      setSelectedTask(null);
      setSelectedLearner(null);
    }
  };

  // Chart.js Options and Data
  
  // 1. Performance Distribution Chart
  const performanceDistributionData = {
    labels: aggregatedStats.performanceLevels.map(level => `${level.level} (${level.range})`),
    datasets: [
      {
        data: aggregatedStats.performanceLevels.map(level => level.count),
        backgroundColor: [
          'rgba(34, 197, 94, 0.7)',  // green for Outstanding
          'rgba(59, 130, 246, 0.7)', // blue for Excellent
          'rgba(234, 179, 8, 0.7)',  // yellow for Proficient
          'rgba(249, 115, 22, 0.7)', // orange for Competent
          'rgba(236, 72, 153, 0.7)', // pink for Partially Achieved
          'rgba(239, 68, 68, 0.7)',  // red for Not Achieved
        ],
        borderColor: [
          'rgba(34, 197, 94, 1)',
          'rgba(59, 130, 246, 1)',
          'rgba(234, 179, 8, 1)',
          'rgba(249, 115, 22, 1)',
          'rgba(236, 72, 153, 1)',
          'rgba(239, 68, 68, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };
  
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 15,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: true,
        text: 'Performance Level Distribution',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.formattedValue;
            const total = allMarks.length;
            const percentage = total > 0 ? ((context.raw / total) * 100).toFixed(1) : 0;
            return `${value} learners (${percentage}%)`;
          }
        }
      }
    },
    cutout: '60%'
  };

  // 2. Topic Performance Radar Chart
  const topicRadarData = {
    labels: Object.keys(topicPerformance),
    datasets: [
      {
        label: 'Average Performance',
        data: Object.values(topicPerformance),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(59, 130, 246)',
      }
    ]
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          display: true
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          stepSize: 20,
          callback: function(value) {
            return value + '%';
          }
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Performance by Topic',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.raw.toFixed(1)}%`;
          }
        }
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (viewMode === 'task' && selectedTask) {
      return (
        <div>
          <button
            onClick={handleBack}
            className="mb-6 text-blue-600 hover:text-blue-800 flex items-center gap-2 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
          </button>
          <PerformanceAnalysis taskId={selectedTask.id} />
        </div>
      );
    }

    if (viewMode === 'learner' && selectedLearner) {
      return (
        <div>
          <button
            onClick={handleBack}
            className="mb-6 text-blue-600 hover:text-blue-800 flex items-center gap-2 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
          </button>
          <LearnerPerformanceDetail 
            learner={selectedLearner} 
            tasks={tasks}
            allMarks={allMarks}
          />
        </div>
      );
    }

    // Overview Mode (Default)
    return (
      <div className="space-y-6">
        {/* Performance Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-blue-800">Total Learners</h3>
            </div>
            <p className="text-2xl font-bold text-blue-900">{aggregatedStats.totalLearners}</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-green-800">Total Tasks</h3>
            </div>
            <p className="text-2xl font-bold text-green-900">{aggregatedStats.totalTasks}</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-purple-800">Average Mark</h3>
            </div>
            <p className="text-2xl font-bold text-purple-900">
              {aggregatedStats.averageMark.toFixed(1)}%
            </p>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-yellow-600" />
              <h3 className="font-medium text-yellow-800">Pass Rate</h3>
            </div>
            <p className="text-2xl font-bold text-yellow-900">
              {aggregatedStats.passRate.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* Charts Grid */}
        {allMarks.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Distribution Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Distribution</h3>
              <div className="h-80">
                <Doughnut data={performanceDistributionData} options={doughnutOptions} />
              </div>
            </div>

            {/* Topic Performance Radar Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Topic Performance</h3>
              <div className="h-80">
                {Object.keys(topicPerformance).length > 0 ? (
                  <Radar data={topicRadarData} options={radarOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">No topic data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tasks Performance */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Tasks Performance</h3>
          </div>
          <div className="p-6">
            {tasks.length > 0 ? (
              <div className="space-y-4">
                {tasks.map(task => {
                  // Calculate stats for this task
                  const taskMarks = allMarks.filter(mark => mark.taskId === task.id);
                  const avgMark = taskMarks.length > 0 
                    ? taskMarks.reduce((sum, mark) => sum + mark.percentageMark, 0) / taskMarks.length 
                    : 0;
                  const passRate = taskMarks.length > 0
                    ? (taskMarks.filter(mark => mark.percentageMark >= 50).length / taskMarks.length) * 100
                    : 0;
                  
                  return (
                    <div 
                      key={task.id} 
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleTaskSelect(task)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{task.name}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.type === 'Exam' ? 'bg-red-100 text-red-800' :
                              task.type === 'Test' ? 'bg-orange-100 text-orange-800' :
                              task.type === 'Project' ? 'bg-purple-100 text-purple-800' :
                              task.type === 'Assignment' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.type}
                            </span>
                            <span>{task.totalMarks} marks</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-600 mb-1">
                            {taskMarks.length} learners assessed
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              avgMark >= 70 ? 'bg-green-100 text-green-800' :
                              avgMark >= 50 ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              Avg: {avgMark.toFixed(1)}%
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              passRate >= 80 ? 'bg-green-100 text-green-800' :
                              passRate >= 50 ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              Pass: {passRate.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="text-xs text-gray-500 mb-1">Performance Distribution</div>
                        <div className="flex w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                          {/* Q4 (Top 25%) */}
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: `${taskMarks.filter(m => m.markQuartile === 'Q4').length / Math.max(taskMarks.length, 1) * 100}%` }}
                          ></div>
                          {/* Q3 (50-75%) */}
                          <div 
                            className="h-full bg-blue-500"
                            style={{ width: `${taskMarks.filter(m => m.markQuartile === 'Q3').length / Math.max(taskMarks.length, 1) * 100}%` }}
                          ></div>
                          {/* Q2 (25-50%) */}
                          <div 
                            className="h-full bg-yellow-500"
                            style={{ width: `${taskMarks.filter(m => m.markQuartile === 'Q2').length / Math.max(taskMarks.length, 1) * 100}%` }}
                          ></div>
                          {/* Q1 (Bottom 25%) */}
                          <div 
                            className="h-full bg-red-500"
                            style={{ width: `${taskMarks.filter(m => m.markQuartile === 'Q1').length / Math.max(taskMarks.length, 1) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Found</h3>
                <p className="text-gray-500">
                  No assessment tasks found for {selectedGrade} {selectedSubject}.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Learners Performance */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Learners Performance</h3>
          </div>
          <div className="p-6">
            {learners.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Learner
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assessments
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Average
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Performance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {learners.map((learner, index) => {
                      const learnerAvg = learner.marks.reduce((sum, mark) => 
                        sum + mark.percentageMark, 0) / learner.marks.length;
                      
                      // Calculate performance trend
                      const sortedMarks = [...learner.marks].sort((a, b) => {
                        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
                        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
                        return dateA - dateB;
                      });
                      
                      const trend = sortedMarks.length > 1 
                        ? sortedMarks[sortedMarks.length - 1].percentageMark - sortedMarks[0].percentageMark
                        : 0;
                      
                      return (
                        <tr 
                          key={`${learner.surname}-${learner.name}-${index}`}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleLearnerSelect(learner)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">
                              {learner.surname}, {learner.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {learner.marks.length} / {tasks.length} tasks
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              learnerAvg >= 70 ? 'bg-green-100 text-green-800' :
                              learnerAvg >= 50 ? 'bg-blue-100 text-blue-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {learnerAvg.toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${
                                trend > 0 ? 'text-green-600' :
                                trend < 0 ? 'text-red-600' :
                                'text-gray-600'
                              }`}>
                                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                              </span>
                              {trend > 0 ? (
                                <TrendingUp className="w-4 h-4 text-green-600" />
                              ) : trend < 0 ? (
                                <TrendingUp className="w-4 h-4 text-red-600 transform rotate-180" />
                              ) : (
                                <Target className="w-4 h-4 text-gray-600" />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button className="text-sm text-blue-600 hover:text-blue-800">
                              View Profile
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Learners Found</h3>
                <p className="text-gray-500">
                  No learner marks recorded for {selectedGrade} {selectedSubject}.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Topic Analysis */}
        {allMarks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Topic Strengths</h3>
              </div>
              <div className="p-6">
                {aggregatedStats.topicsStrengths.length > 0 ? (
                  <div className="space-y-4">
                    {aggregatedStats.topicsStrengths.map((topic, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{topic.topic}</div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                            <div 
                              className="bg-green-600 h-2.5 rounded-full" 
                              style={{ width: `${topic.averagePercentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="ml-4 text-sm font-medium text-green-700">
                          {topic.averagePercentage.toFixed(1)}%
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">No topic data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Weaknesses */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Areas for Improvement</h3>
              </div>
              <div className="p-6">
                {aggregatedStats.topicsWeaknesses.length > 0 ? (
                  <div className="space-y-4">
                    {aggregatedStats.topicsWeaknesses.map((topic, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">{topic.topic}</div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
                            <div 
                              className="bg-red-600 h-2.5 rounded-full" 
                              style={{ width: `${topic.averagePercentage}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="ml-4 text-sm font-medium text-red-700">
                          {topic.averagePercentage.toFixed(1)}%
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">No topic data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Performance Analysis</h1>
        </div>
        
        {/* Filters */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 mb-4"
        >
          <Filter className="w-4 h-4" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grade
              </label>
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select grade...</option>
                {availableGrades.map(grade => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select subject...</option>
                {availableSubjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Term
              </label>
              <select
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {terms.map(term => (
                  <option key={term} value={term}>{term === 'all' ? 'All Terms' : term}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      {renderContent()}
    </div>
  );
};

// Learner Performance Detail Component
const LearnerPerformanceDetail = ({ learner, tasks, allMarks }) => {
  // Chart.js Options and Data
  
  // Performance Line Chart
  const prepareLineChartData = () => {
    // Sort marks by date
    const sortedMarks = [...learner.marks].sort((a, b) => {
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
      return dateA - dateB;
    });
    
    // Find task names for labels
    const labels = sortedMarks.map(mark => {
      const task = tasks.find(t => t.id === mark.taskId);
      return task?.name || 'Unknown Task';
    });
    
    return {
      labels,
      datasets: [
        {
          label: 'Learner Performance',
          data: sortedMarks.map(mark => mark.percentageMark),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          tension: 0.3,
        },
        {
          label: 'Class Average',
          data: sortedMarks.map(mark => mark.classAverage),
          borderColor: 'rgb(156, 163, 175)',
          backgroundColor: 'rgba(156, 163, 175, 0.5)',
          borderDash: [5, 5],
          tension: 0.3,
        }
      ]
    };
  };
  
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Performance Over Time',
        font: {
          size: 14
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(1) + '%';
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        title: {
          display: true,
          text: 'Percentage'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Assessment'
        }
      }
    }
  };

  // Calculate average performance
  const learnerAvg = learner.marks.reduce((sum, mark) => 
    sum + mark.percentageMark, 0) / learner.marks.length;

  // Sort marks by date
  const sortedMarks = [...learner.marks].sort((a, b) => {
    const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
    const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
    return dateA - dateB;
  });

  return (
    <div className="space-y-6">
      {/* Learner Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-900">
            {learner.surname}, {learner.name}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-700">Average Performance</p>
            <p className={`text-xl font-bold ${
              learnerAvg >= 70 ? 'text-green-600' :
              learnerAvg >= 50 ? 'text-blue-600' :
              'text-red-600'
            }`}>
              {learnerAvg.toFixed(1)}%
            </p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">Assessments Completed</p>
            <p className="text-xl font-bold text-gray-900">
              {learner.marks.length} / {tasks.length}
            </p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">Performance Trend</p>
            <div className="flex items-center gap-2">
              {sortedMarks.length > 1 && (
                <>
                  <p className={`text-xl font-bold ${
                    sortedMarks[sortedMarks.length - 1].percentageMark - sortedMarks[0].percentageMark > 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}>
                    {sortedMarks[sortedMarks.length - 1].percentageMark - sortedMarks[0].percentageMark > 0 ? '+' : ''}
                    {(sortedMarks[sortedMarks.length - 1].percentageMark - sortedMarks[0].percentageMark).toFixed(1)}%
                  </p>
                  {sortedMarks[sortedMarks.length - 1].percentageMark - sortedMarks[0].percentageMark > 0 ? (
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  ) : (
                    <TrendingUp className="w-5 h-5 text-red-600 transform rotate-180" />
                  )}
                </>
              )}
              {sortedMarks.length <= 1 && (
                <p className="text-xl font-bold text-gray-500">-</p>
              )}
            </div>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">Latest Assessment</p>
            <p className="text-xl font-bold text-gray-900">
              {sortedMarks.length > 0 
                ? sortedMarks[sortedMarks.length - 1].percentageMark.toFixed(1) + '%'
                : '-'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Performance Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Over Time</h3>
        
        {sortedMarks.length > 1 ? (
          <div className="h-80">
            <Line data={prepareLineChartData()} options={lineOptions} />
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">Not enough data to show performance trend</p>
          </div>
        )}
      </div>

      {/* Task Details */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Assessment Results</h3>
        </div>
        <div className="p-6">
          {sortedMarks.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Task
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mark
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Class Average
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedMarks.map((mark, index) => {
                    // Find task
                    const task = tasks.find(t => t.id === mark.taskId);
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">
                            {task?.name || 'Unknown Task'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {task?.type || 'Unknown Type'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {mark.createdAt instanceof Date 
                              ? mark.createdAt.toLocaleDateString()
                              : new Date(mark.createdAt).toLocaleDateString()
                            }
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            mark.percentageMark >= 70 ? 'bg-green-100 text-green-800' :
                            mark.percentageMark >= 50 ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {mark.percentageMark.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {mark.classAverage.toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {mark.percentageMark > mark.classAverage ? (
                              <>
                                <span className="text-sm text-green-600">
                                  +{(mark.percentageMark - mark.classAverage).toFixed(1)}%
                                </span>
                                <TrendingUp className="w-4 h-4 text-green-600" />
                              </>
                            ) : mark.percentageMark < mark.classAverage ? (
                              <>
                                <span className="text-sm text-red-600">
                                  {(mark.percentageMark - mark.classAverage).toFixed(1)}%
                                </span>
                                <TrendingUp className="w-4 h-4 text-red-600 transform rotate-180" />
                              </>
                            ) : (
                              <span className="text-sm text-gray-600">
                                At average
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No assessment results found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GradePerformanceAnalysis;